import { AssignButton, ownerWhere } from "@/components/AssignButton";
import { Countdown, DemoStatusBadge, OutcomeBadge } from "@/components/DemoBits";
import { DemoContentCard } from "@/components/DemoContentCard";
import { ImportReport } from "@/components/ImportReport";
import { ShopSettingsForm } from "@/components/ShopSettingsForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input, Select, Textarea } from "@/components/ui/input";
import { EmptyState, ErrorBox, Loading, Tabs } from "@/components/ui/misc";
import { type Me, api, openDemoWindow, q } from "@/lib/api";
import { cn, fmtDateTime, relative, stageLabel } from "@/lib/utils";
import type {
  ActivityDTO,
  AgentDTO,
  DemoDTO,
  ImportJobDTO,
  PresentationDTO,
  ProspectDTO,
  ProspectOutcome,
  ProspectStage,
  SlideImageBody,
} from "@mm-sales/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Copy,
  ExternalLink,
  ImagePlus,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  Upload,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ImportProgress, MergedSummary, sourceLabel } from "./NewDemoPage";

type Tab = "overview" | "imports" | "presentation" | "activity";

export function ProspectPage() {
  const { id = "" } = useParams();
  const me = useOutletContext<Me>();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const prospect = useQuery({
    queryKey: ["prospect", id],
    queryFn: () => q.prospect(id),
  });
  const demos = useQuery({
    queryKey: ["demos", id],
    queryFn: () => q.prospectDemos(id),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["prospect", id] });
    qc.invalidateQueries({ queryKey: ["demos", id] });
    qc.invalidateQueries({ queryKey: ["activity", id] });
    qc.invalidateQueries({ queryKey: ["prospects"] });
  };
  const run = (fn: () => Promise<unknown>) =>
    fn()
      .then(refresh)
      .catch((e: Error) => setErr(e.message));

  if (prospect.isLoading) return <Loading />;
  if (!prospect.data)
    return (
      <div className="p-8">
        <ErrorBox message="Prospect not found or you don't have access." />
      </div>
    );
  const p = prospect.data;
  const active =
    demos.data?.items.find((d) => d.status === "active" || d.status === "provisioning") ?? null;
  const canManage = me.agent.role !== "agent";

  return (
    <div className="p-6 lg:p-8">
      <Link
        to="/app"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> My demos
      </Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {p.logoUrl ? (
            <img src={p.logoUrl} alt="" className="h-14 w-14 rounded-lg border object-cover" />
          ) : (
            <div
              className="flex h-14 w-14 items-center justify-center rounded-lg text-xl font-bold text-white"
              style={{ background: p.brandColor ?? "#174ae7" }}
            >
              {p.businessName[0]}
            </div>
          )}
          <div>
            <div className="flex items-center gap-1">
              <h1 className="text-2xl font-semibold tracking-tight">{p.businessName}</h1>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Edit business details"
                title="Edit details"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>
                {[p.cuisine, [p.city, p.state].filter(Boolean).join(", ")]
                  .filter(Boolean)
                  .join(" · ") || "No details yet"}
              </span>
              <OutcomeBadge outcome={p.outcome} />
              <Badge variant="outline">{stageLabel[p.stage] ?? p.stage}</Badge>
              {p.owner ? (
                <span className="inline-flex items-center gap-1">
                  <UserRound className="h-3.5 w-3.5" /> {p.owner.name}
                  {ownerWhere(p.owner) ? ` · ${ownerWhere(p.owner)}` : ""}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {active?.status === "active" ? (
            <Button onClick={() => openDemoWindow(active.shareToken)}>
              <Play className="h-4 w-4" /> Launch
            </Button>
          ) : null}
          <OutcomeMenu prospectId={id} outcome={p.outcome} onDone={refresh} />
          {canManage ? (
            <AssignButton prospectId={id} currentOwner={p.ownerAgentId} onDone={refresh} />
          ) : null}
        </div>
      </div>

      {err ? (
        <div className="mb-4">
          <ErrorBox message={err} />
        </div>
      ) : null}

      <div className="mb-5">
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { value: "overview", label: "Demo" },
            { value: "imports", label: "Imports" },
            { value: "presentation", label: "Presentation" },
            { value: "activity", label: "Activity" },
          ]}
        />
      </div>

      {tab === "overview" ? (
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {active ? (
              <DemoCard
                demo={active}
                onExtend={(days) => run(() => api.post(`/v1/demos/${active.id}/extend`, { days }))}
                onRevoke={() => run(() => api.post(`/v1/demos/${active.id}/revoke`))}
              />
            ) : (
              <EmptyState
                title="No active demo"
                body={
                  p.outcome === "open"
                    ? "Build one to get a share link and streamed devices."
                    : "This prospect is closed."
                }
                action={
                  p.outcome === "open" ? (
                    <Button
                      loading={false}
                      onClick={() => run(() => api.post("/v1/demos", { prospectId: id }))}
                    >
                      Build demo
                    </Button>
                  ) : undefined
                }
              />
            )}
            <Card className="mt-5">
              <CardHeader>
                <CardTitle>Shop settings for the demo</CardTitle>
                <CardDescription>
                  The Shop+ settings the demo store is created with (register, POS screens, online
                  checkout, tips). Saved on the prospect; applied when a demo is built
                  {active ? " and to the live demo the next time an import is applied" : ""}.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ShopSettingsForm
                  key={JSON.stringify(p.shopSettings ?? {})}
                  value={p.shopSettings ?? null}
                  saving={false}
                  onSave={(v) => run(() => api.patch(`/v1/prospects/${id}`, { shopSettings: v }))}
                />
              </CardContent>
            </Card>
            <div className="mt-5">
              <DemoContentCard prospect={p} onChange={() => refresh()} />
            </div>
            {demos.data?.items.filter((d) => d.id !== active?.id).length ? (
              <div className="mt-5">
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Previous demos</h3>
                <div className="divide-y rounded-lg border bg-card">
                  {demos.data.items
                    .filter((d) => d.id !== active?.id)
                    .map((d) => (
                      <div
                        key={d.id}
                        className="flex items-center justify-between px-4 py-2.5 text-sm"
                      >
                        <span>{fmtDateTime(d.createdAt)}</span>
                        <span className="text-muted-foreground">opened {d.openCount}×</span>
                        <span className="flex items-center gap-2">
                          <DemoStatusBadge status={d.status} />
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Remove this demo: its link stops working and its demo account is purged"
                            onClick={() => run(() => api.del(`/v1/demos/${d.id}`))}
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Remove
                          </Button>
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}
          </div>
          <div className="space-y-4">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle>Contact</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <div className="font-medium">
                  {[p.contact.firstName, p.contact.lastName].filter(Boolean).join(" ") || "—"}
                </div>
                {p.contact.title ? (
                  <div className="text-muted-foreground">{p.contact.title}</div>
                ) : null}
                <div className="text-muted-foreground">{p.contact.email || "no email"}</div>
                <div className="text-muted-foreground">{p.contact.phone || "no phone"}</div>
                {p.website ? (
                  <a
                    href={p.website}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    {p.website.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
              </CardContent>
            </Card>
            <BuiltSiteCard prospectId={id} value={p.builtSiteUrl ?? ""} onDone={refresh} />
            <BrandCard prospect={p} onDone={refresh} />
            <StageCard prospectId={id} stage={p.stage} onDone={refresh} />
            {p.slaDeadlineAt && p.outcome === "open" ? (
              <Card>
                <CardHeader>
                  <CardTitle>Follow-up window</CardTitle>
                  <CardDescription>
                    Moves to the internal team if there's no activity by then.
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-sm">
                  <Countdown to={p.slaDeadlineAt} />{" "}
                  <span className="text-muted-foreground">
                    · last activity {relative(p.lastActivityAt)}
                  </span>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === "imports" ? (
        <ImportsTab
          prospectId={id}
          website={p.website}
          hasDemo={Boolean(active)}
          me={me}
          onChanged={refresh}
        />
      ) : null}
      {tab === "presentation" ? (
        <PresentationTab prospectId={id} prospectWebsite={prospect.data?.website ?? ""} me={me} />
      ) : null}
      {tab === "activity" ? <ActivityTab prospectId={id} /> : null}
      {editing ? (
        <EditDetailsDialog
          prospect={p}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/** Name, description line (type · city, state), website and the contact person. */
function EditDetailsDialog({
  prospect,
  onClose,
  onSaved,
}: {
  prospect: ProspectDTO;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState({
    businessName: prospect.businessName,
    cuisine: prospect.cuisine ?? "",
    city: prospect.city ?? "",
    state: prospect.state ?? "",
    website: prospect.website ?? "",
    firstName: prospect.contact.firstName ?? "",
    lastName: prospect.contact.lastName ?? "",
    title: prospect.contact.title ?? "",
    email: prospect.contact.email ?? "",
    phone: prospect.contact.phone ?? "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));
  const name = f.businessName.trim();
  const m = useMutation({
    mutationFn: () =>
      api.patch(`/v1/prospects/${prospect.id}`, {
        businessName: name,
        cuisine: f.cuisine.trim(),
        city: f.city.trim(),
        state: f.state.trim(),
        website: f.website.trim(),
        contact: {
          firstName: f.firstName.trim(),
          lastName: f.lastName.trim(),
          title: f.title.trim(),
          email: f.email.trim(),
          phone: f.phone.trim(),
        },
      }),
    onSuccess: onSaved,
  });
  const preview =
    [f.cuisine.trim(), [f.city.trim(), f.state.trim()].filter(Boolean).join(", ")]
      .filter(Boolean)
      .join(" · ") || "No details yet";
  return (
    <Dialog open onClose={onClose} title="Edit details" className="max-w-xl">
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (name) m.mutate();
        }}
      >
        <div className="space-y-3">
          <Field label="Business name">
            <Input value={f.businessName} onChange={set("businessName")} autoFocus />
          </Field>
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_6rem]">
            <Field label="Description">
              <Input
                value={f.cuisine}
                onChange={set("cuisine")}
                placeholder="Hawaiian Restaurant"
              />
            </Field>
            <Field label="City">
              <Input value={f.city} onChange={set("city")} placeholder="Kahului" />
            </Field>
            <Field label="State">
              <Input value={f.state} onChange={set("state")} placeholder="HI" />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">Shown under the name as: {preview}</p>
          <Field label="Their website">
            <Input value={f.website} onChange={set("website")} placeholder="https://" />
          </Field>
        </div>
        <div className="space-y-3 border-t pt-4">
          <div className="text-sm font-medium">Contact</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="First name">
              <Input value={f.firstName} onChange={set("firstName")} />
            </Field>
            <Field label="Last name">
              <Input value={f.lastName} onChange={set("lastName")} />
            </Field>
            <Field label="Role (optional)">
              <Input value={f.title} onChange={set("title")} placeholder="Owner" />
            </Field>
            <Field label="Phone">
              <Input value={f.phone} onChange={set("phone")} type="tel" />
            </Field>
          </div>
          <Field label="Email">
            <Input value={f.email} onChange={set("email")} type="email" />
          </Field>
        </div>
        {m.error ? <ErrorBox message={(m.error as Error).message} /> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!name} loading={m.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DemoCard({
  demo,
  onExtend,
  onRevoke,
}: {
  demo: DemoDTO;
  onExtend: (days: number) => void;
  onRevoke: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(demo.shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            Live demo <DemoStatusBadge status={demo.status} />
          </CardTitle>
          <CardDescription>
            {demo.status === "active" ? (
              <>
                Expires in <Countdown to={demo.expiresAt} compact /> · opened {demo.openCount}×{" "}
                {demo.lastOpenedAt ? `· last ${relative(demo.lastOpenedAt)}` : ""}
              </>
            ) : (
              (demo.error ?? "Building the demo org…")
            )}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Share with the prospect
          </div>
          <div className="flex gap-2">
            <Input readOnly value={demo.shareUrl} className="font-mono text-xs" />
            <Button variant="outline" onClick={copy}>
              <Copy className="h-4 w-4" /> {copied ? "Copied" : "Copy"}
            </Button>
            <a href={demo.shareUrl} target="_blank" rel="noreferrer">
              <Button variant="outline">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {demo.apps.map((a) => (
            <Badge key={a} variant="secondary" className="capitalize">
              {a}
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 border-t pt-4">
          {demo.adminUrl ? (
            <a href={demo.adminUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm">
                Open admin app
              </Button>
            </a>
          ) : null}
          {demo.websiteUrl ? (
            <a href={demo.websiteUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm">
                Open website
              </Button>
            </a>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => onExtend(7)}>
            <RefreshCw className="h-3.5 w-3.5" /> Extend 7 days
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-destructive"
            onClick={() =>
              confirm("Revoke this demo? The prospect's link stops working.") && onRevoke()
            }
          >
            Revoke
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StageCard({
  prospectId,
  stage,
  onDone,
}: {
  prospectId: string;
  stage: ProspectStage;
  onDone: () => void;
}) {
  const m = useMutation({
    mutationFn: (s: string) => api.patch(`/v1/prospects/${prospectId}`, { stage: s }),
    onSuccess: onDone,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Stage</CardTitle>
      </CardHeader>
      <CardContent>
        <Select value={stage} onChange={(e) => m.mutate(e.target.value)}>
          {Object.entries(stageLabel).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </Select>
      </CardContent>
    </Card>
  );
}

function OutcomeMenu({
  prospectId,
  outcome,
  onDone,
}: {
  prospectId: string;
  outcome: ProspectOutcome;
  onDone: () => void;
}) {
  const [open, setOpen] = useState<null | "won" | "lost">(null);
  const [reason, setReason] = useState("");
  const [value, setValue] = useState("");
  const m = useMutation({
    mutationFn: (o: ProspectOutcome) =>
      api.post(`/v1/prospects/${prospectId}/outcome`, {
        outcome: o,
        reason: reason || undefined,
        value: value ? Number(value) : undefined,
      }),
    onSuccess: () => {
      setOpen(null);
      onDone();
    },
  });
  if (outcome !== "open")
    return (
      <Button variant="outline" onClick={() => m.mutate("open")}>
        Reopen
      </Button>
    );
  return (
    <>
      <Button variant="outline" className="text-success" onClick={() => setOpen("won")}>
        Mark won
      </Button>
      <Button variant="outline" className="text-destructive" onClick={() => setOpen("lost")}>
        Mark lost
      </Button>
      <Dialog
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open === "won" ? "Mark as won" : "Mark as lost"}
      >
        <div className="space-y-4">
          {open === "won" ? (
            <Field label="Monthly value (USD)">
              <Input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="299"
              />
            </Field>
          ) : null}
          <Field label={open === "won" ? "Notes (optional)" : "Why did we lose it?"}>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(null)}>
              Cancel
            </Button>
            <Button
              loading={m.isPending}
              variant={open === "won" ? "default" : "destructive"}
              onClick={() => m.mutate(open!)}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

function ImportsTab({
  prospectId,
  website,
  hasDemo,
  me,
  onChanged,
}: {
  prospectId: string;
  website: string;
  hasDemo: boolean;
  me: Me;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(website);
  const [err, setErr] = useState<string | null>(null);
  const imports = useQuery({
    queryKey: ["imports", prospectId],
    queryFn: () => q.prospectImports(prospectId),
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (j) => j.status === "queued" || j.status === "running" || j.imageGen?.status === "running",
      )
        ? 1200
        : false,
  });
  const start = useMutation({
    mutationFn: (platform: string) =>
      api.post("/v1/imports", {
        prospectId,
        platform,
        sourceUrl: platform === "website" ? url : undefined,
      }),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ["imports", prospectId] });
    },
    onError: (e) => setErr(e.message),
  });
  const apply = useMutation({
    mutationFn: (jobId: string) => api.post(`/v1/imports/${jobId}/apply`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["imports", prospectId] });
      onChanged();
    },
    onError: (e) => setErr(e.message),
  });
  // Several finished imports can be applied as one merged shop (menu from the
  // richest, photos from the others, brand from the website, hours from Google).
  const reviewJobs = (imports.data?.items ?? []).filter(
    (j) => j.status === "review" && !j.locationId,
  );
  const [unticked, setUnticked] = useState<Record<string, boolean>>({});
  const ticked = reviewJobs.filter((j) => !unticked[j.id]);
  const merge = useQuery({
    queryKey: ["merge-preview", prospectId, ticked.map((j) => j.id).join(",")],
    queryFn: () =>
      q.mergePreview(
        prospectId,
        ticked.map((j) => j.id),
      ),
    enabled: ticked.length >= 2,
  });
  const genImages = useMutation({
    mutationFn: (jobId: string) => api.post(`/v1/imports/${jobId}/generate-images`, { limit: 40 }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["imports", prospectId] }),
    onError: (e) => setErr(e.message),
  });
  const applyMerged = useMutation({
    mutationFn: () =>
      api.post("/v1/imports/apply-merged", { prospectId, jobIds: ticked.map((j) => j.id) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["imports", prospectId] });
      onChanged();
    },
    onError: (e) => setErr(e.message),
  });
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>Import</CardTitle>
          <CardDescription>Nothing is applied until you say so.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {err ? <ErrorBox message={err} /> : null}
          <Field label="Website URL">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value.trim())}
              placeholder="https://…"
            />
          </Field>
          <Button
            className="w-full"
            variant="outline"
            disabled={!/^https?:\/\//.test(url)}
            loading={start.isPending}
            onClick={() => start.mutate("website")}
          >
            From their website
          </Button>
          <Button
            className="w-full"
            variant="outline"
            disabled={!me.capabilities.importers.google_places}
            loading={start.isPending}
            onClick={() => start.mutate("google_places")}
          >
            From Google Business Profile
          </Button>
          <p className="text-xs text-muted-foreground">
            Paste a website, an Uber Eats / DoorDash / Grubhub store, or a menu PDF or photo — the
            AI reads document menus into categories and prices.
          </p>
        </CardContent>
      </Card>
      <div className="space-y-3 lg:col-span-2">
        {imports.data?.items.length === 0 ? (
          <EmptyState
            title="No imports yet"
            body="Pull the menu and branding from their website or Google listing."
          />
        ) : null}
        {reviewJobs.length >= 2 ? (
          <div className="space-y-2 rounded-lg border border-primary/40 bg-accent p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="font-medium">
                Apply {ticked.length} sources merged
                <span className="ml-2 font-normal text-muted-foreground">
                  {hasDemo ? "into the live demo" : "to the prospect"}
                </span>
              </span>
              <Button
                size="sm"
                disabled={ticked.length < 2}
                loading={applyMerged.isPending}
                onClick={() => applyMerged.mutate()}
              >
                Apply merged{merge.data ? ` · ${merge.data.report.totals.items} items` : ""}
              </Button>
            </div>
            <div className="flex flex-wrap gap-3 text-xs">
              {reviewJobs.map((j) => (
                <label key={j.id} className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={!unticked[j.id]}
                    onChange={(e) => setUnticked((u) => ({ ...u, [j.id]: !e.target.checked }))}
                  />
                  {sourceLabel(j.platform)} · {j.shop?.items.length ?? 0} items
                </label>
              ))}
            </div>
            {ticked.length >= 2 ? (
              <MergedSummary merge={merge.data} loading={merge.isPending} />
            ) : null}
          </div>
        ) : null}
        {(imports.data?.items ?? []).map((j: ImportJobDTO) => (
          <div key={j.id} className="space-y-3">
            <ImportProgress
              job={j}
              label={sourceLabel(j.platform)}
              onGenerateImages={
                me.capabilities.images && (j.status === "review" || j.status === "applied")
                  ? () => genImages.mutate(j.id)
                  : undefined
              }
              generating={genImages.isPending}
            />
            {j.status === "review" ? (
              <div className="flex items-center justify-between rounded-lg border bg-accent/40 px-4 py-3 text-sm">
                <span>
                  {hasDemo
                    ? "Apply this one to the live demo now?"
                    : "Apply this one to the prospect (used when the next demo is built)?"}
                </span>
                <Button
                  size="sm"
                  variant={reviewJobs.length >= 2 ? "outline" : "default"}
                  loading={apply.isPending}
                  onClick={() => apply.mutate(j.id)}
                >
                  Apply {j.shop?.items.length ?? 0} items
                </Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function PresentationTab({
  prospectId,
  prospectWebsite,
  me,
}: {
  prospectId: string;
  prospectWebsite: string;
  me: Me;
}) {
  const qc = useQueryClient();
  const pres = useQuery({
    queryKey: ["presentation", prospectId],
    queryFn: () => q.presentation(prospectId),
  });
  const reset = useMutation({
    mutationFn: () => api.post(`/v1/prospects/${prospectId}/presentation/reset`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation", prospectId] }),
  });
  const save = useMutation({
    mutationFn: (slides: PresentationDTO["slides"]) =>
      api.put(`/v1/prospects/${prospectId}/presentation`, { slides }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation", prospectId] }),
  });
  const generate = useMutation({
    mutationFn: () =>
      api.post<PresentationDTO>(`/v1/prospects/${prospectId}/presentation/generate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation", prospectId] }),
  });
  const slideImage = useMutation({
    mutationFn: ({ slideId, body }: { slideId: string; body: SlideImageBody | FormData }) =>
      body instanceof FormData
        ? api.upload<PresentationDTO>(
            `/v1/prospects/${prospectId}/presentation/slides/${slideId}/image`,
            body,
          )
        : api.post<PresentationDTO>(
            `/v1/prospects/${prospectId}/presentation/slides/${slideId}/image`,
            body,
          ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["presentation", prospectId] }),
  });
  const [imgBusy, setImgBusy] = useState<string | null>(null);
  const fileFor = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const setImage = async (slideId: string, body: SlideImageBody | FormData) => {
    setImgBusy(slideId);
    try {
      await slideImage.mutateAsync({ slideId, body });
    } finally {
      setImgBusy(null);
    }
  };
  if (!pres.data) return <Loading />;
  const slides = pres.data.slides;
  const saveSlides = (next: PresentationDTO["slides"]) => save.mutate(next);
  const addSlide = (after: number) => {
    const id = `custom_${Date.now().toString(36)}`;
    const next = [...slides];
    next.splice(after + 1, 0, {
      id,
      kind: "custom",
      title: "New slide",
      subtitle: "",
      points: [
        { title: "First point", detail: "" },
        { title: "Second point", detail: "" },
      ],
    });
    saveSlides(next);
  };
  const removeSlide = (i: number) => {
    if (slides.length <= 1) return;
    saveSlides(slides.filter((_, j) => j !== i));
  };
  const moveSlide = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= slides.length) return;
    const next = [...slides];
    [next[i], next[j]] = [next[j]!, next[i]!];
    saveSlides(next);
  };
  const addPoint = (i: number) => {
    const next = [...slides];
    next[i] = { ...slides[i]!, points: [...slides[i]!.points, { title: "New point", detail: "" }] };
    saveSlides(next);
  };
  const removePoint = (i: number, j: number) => {
    const next = [...slides];
    next[i] = { ...slides[i]!, points: slides[i]!.points.filter((_, k) => k !== j) };
    saveSlides(next);
  };
  const audit = pres.data.audit;
  const research = pres.data.research;
  const gaps = audit?.findings.filter((f) => f.severity === "gap") ?? [];
  const oks = audit?.findings.filter((f) => f.severity !== "gap") ?? [];
  const writer = me.capabilities.ai === "openai" ? "OpenAI" : "Claude";
  const readFrom = [
    audit ? `their website (${audit.pagesChecked} pages)` : null,
    research?.presence.length ? `${research.presence.length} web results` : null,
  ]
    .filter(Boolean)
    .join(" and ");
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Business review</CardTitle>
              <CardDescription>
                {pres.data.generatedBy === "ai"
                  ? `Written by ${writer} from ${readFrom || "what we know"}. Opens with why MyManager, ends with the close.`
                  : pres.data.generatedBy === "rules"
                    ? `Built from ${readFrom || "what we know"}. Add ANTHROPIC_API_KEY to the API for slides written by Claude.`
                    : "Stock deck. Generate one to talk about their actual gaps — their website plus what reviews, listings and socials say."}
                {pres.data.generatedAt ? ` Generated ${relative(pres.data.generatedAt)}.` : ""}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => reset.mutate()}>
                Reset to default
              </Button>
              <Button
                size="sm"
                loading={generate.isPending}
                onClick={() => generate.mutate()}
                title={
                  prospectWebsite
                    ? "Re-read their website and search the web, then rewrite the deck"
                    : "No website on the prospect: the deck is written from web search only"
                }
              >
                <RefreshCw className="h-4 w-4" />
                {pres.data.generatedBy === "default" ? "Generate" : "Regenerate"}
              </Button>
            </div>
          </div>
        </CardHeader>
        {generate.isError ? (
          <CardContent>
            <ErrorBox message={(generate.error as Error).message} />
          </CardContent>
        ) : null}
        {audit ? (
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-destructive">
                Gaps we found ({gaps.length})
              </div>
              <ul className="space-y-1.5">
                {gaps.map((f) => (
                  <li
                    key={f.key}
                    className="rounded-md border border-destructive/20 bg-destructive/5 p-2"
                  >
                    <div className="text-sm font-medium">{f.title}</div>
                    <div className="text-xs text-muted-foreground">{f.detail}</div>
                  </li>
                ))}
                {!gaps.length ? (
                  <li className="text-sm text-muted-foreground">
                    Nothing obvious — a strong site.
                  </li>
                ) : null}
              </ul>
            </div>
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-success">
                What they already have
              </div>
              <ul className="space-y-1.5">
                {oks.map((f) => (
                  <li key={f.key} className="rounded-md border p-2">
                    <div className="text-sm font-medium">{f.title}</div>
                    <div className="text-xs text-muted-foreground">{f.detail}</div>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        ) : null}
      </Card>
      {research ? (
        <Card>
          <CardHeader>
            <CardTitle>Across the web</CardTitle>
            <CardDescription>
              {research.presence.length
                ? `${research.presence.length} results from ${[...new Set(research.presence.map((x) => x.site))].slice(0, 6).join(", ")}${research.by === "ai" ? `, read by ${writer}` : ""}.`
                : "No web results. Add BRAVE_SEARCH_API_KEY to the API to search reviews, listings and socials."}
              {research.summary ? ` ${research.summary}` : ""}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-destructive">
                Problems we can fix ({research.problems.length})
              </div>
              <ul className="space-y-1.5">
                {research.problems.map((f) => (
                  <li
                    key={f.title}
                    className="rounded-md border border-destructive/20 bg-destructive/5 p-2"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {f.title}
                      {f.area ? (
                        <span className="rounded bg-background px-1.5 py-0.5 text-[10px] font-normal uppercase tracking-wide text-muted-foreground">
                          {f.area}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground">{f.detail}</div>
                    {f.sources.length ? (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {f.sources.map((u) => (
                          <a
                            key={u}
                            href={u}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] text-primary underline"
                          >
                            {u.replace(/^https?:\/\/(www\.)?/, "").slice(0, 40)}
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </li>
                ))}
                {!research.problems.length ? (
                  <li className="text-sm text-muted-foreground">Nothing beyond the website.</li>
                ) : null}
              </ul>
            </div>
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-success">
                Strengths
              </div>
              <ul className="space-y-1.5">
                {research.strengths.map((f) => (
                  <li key={f.title} className="rounded-md border p-2">
                    <div className="text-sm font-medium">{f.title}</div>
                    <div className="text-xs text-muted-foreground">{f.detail}</div>
                  </li>
                ))}
              </ul>
              {research.presence.length ? (
                <>
                  <div className="mb-1.5 mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Where they show up
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {research.presence.slice(0, 14).map((x) => (
                      <a
                        key={x.url}
                        href={x.url}
                        target="_blank"
                        rel="noreferrer"
                        title={x.title || x.url}
                        className="rounded-full border px-2 py-0.5 text-[11px] hover:bg-muted"
                      >
                        {x.site}
                        <span className="text-muted-foreground"> · {x.kind}</span>
                      </a>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}
      <p className="text-sm text-muted-foreground">
        The slides the prospect sees under "Presentation". Edit titles and points inline. Slide 1 is
        the MyManager pitch and the last slide is the close; the middle is about them.
      </p>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          const slideId = fileFor.current;
          e.target.value = "";
          if (!f || !slideId) return;
          const fd = new FormData();
          fd.append("file", f);
          void setImage(slideId, fd);
        }}
      />
      {slides.map((s, i) => (
        <Card key={s.id}>
          <CardHeader>
            <div className="flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                {i + 1}
              </span>
              <Input
                defaultValue={s.title}
                className="max-w-md font-semibold"
                onBlur={(e) => {
                  const next = [...slides];
                  next[i] = { ...s, title: e.target.value };
                  saveSlides(next);
                }}
              />
              <span className="ml-auto flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  title="Move up"
                  disabled={i === 0}
                  onClick={() => moveSlide(i, -1)}
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Move down"
                  disabled={i === slides.length - 1}
                  onClick={() => moveSlide(i, 1)}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Add a slide after this one"
                  onClick={() => addSlide(i)}
                >
                  <Plus className="h-3.5 w-3.5" /> Slide
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Remove this slide"
                  disabled={slides.length <= 1}
                  onClick={() => removeSlide(i)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </span>
            </div>
            <Input
              defaultValue={s.subtitle}
              className="text-muted-foreground"
              onBlur={(e) => {
                const next = [...slides];
                next[i] = { ...s, subtitle: e.target.value };
                saveSlides(next);
              }}
            />
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-[200px_1fr]">
            <div>
              <div className="relative aspect-[3/2] overflow-hidden rounded-md border bg-muted">
                {s.imageUrl ? (
                  <img src={s.imageUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                    No image · uses the cover photo
                  </div>
                )}
                {imgBusy === s.id ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-xs">
                    Working…
                  </div>
                ) : null}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  title="Upload an image"
                  disabled={imgBusy !== null}
                  onClick={() => {
                    fileFor.current = s.id;
                    fileInput.current?.click();
                  }}
                >
                  <Upload className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  title="Use an image URL"
                  disabled={imgBusy !== null}
                  onClick={() => {
                    const url = window.prompt("Image URL", s.imageUrl ?? "");
                    if (url?.trim()) void setImage(s.id, { url: url.trim() });
                  }}
                >
                  <ImagePlus className="h-3.5 w-3.5" />
                </Button>
                {me.capabilities.images ? (
                  <Button
                    variant="outline"
                    size="sm"
                    title="Generate an image for this slide with AI (paid)"
                    disabled={imgBusy !== null}
                    onClick={() => void setImage(s.id, { generate: true })}
                  >
                    <Sparkles className="h-3.5 w-3.5" /> AI
                  </Button>
                ) : null}
                {s.imageUrl ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Remove image"
                    disabled={imgBusy !== null}
                    onClick={() => void setImage(s.id, { remove: true })}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </div>
              {slideImage.isError && imgBusy === null ? (
                <p className="mt-1 text-[11px] text-destructive">
                  {(slideImage.error as Error).message}
                </p>
              ) : null}
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              {s.points.map((pt, j) => (
                <div key={`${s.id}-${j}`} className="relative rounded-md border p-2">
                  <button
                    type="button"
                    className="absolute right-1 top-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive"
                    title="Remove point"
                    onClick={() => removePoint(i, j)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <Input
                    defaultValue={pt.title}
                    className="mb-1 h-8 pr-6 font-medium"
                    onBlur={(e) => {
                      const next = [...slides];
                      const points = [...s.points];
                      points[j] = { ...pt, title: e.target.value };
                      next[i] = { ...s, points };
                      saveSlides(next);
                    }}
                  />
                  <Input
                    defaultValue={pt.detail}
                    className="h-8 text-muted-foreground"
                    onBlur={(e) => {
                      const next = [...slides];
                      const points = [...s.points];
                      points[j] = { ...pt, detail: e.target.value };
                      next[i] = { ...s, points };
                      saveSlides(next);
                    }}
                  />
                </div>
              ))}
              {s.points.length < 6 ? (
                <button
                  type="button"
                  className="flex min-h-[60px] items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground hover:bg-muted"
                  onClick={() => addPoint(i)}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add point
                </button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ))}
      <Button variant="outline" onClick={() => addSlide(slides.length - 1)}>
        <Plus className="h-4 w-4" /> Add slide at the end
      </Button>
    </div>
  );
}

function ActivityTab({ prospectId }: { prospectId: string }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const act = useQuery({
    queryKey: ["activity", prospectId],
    queryFn: () => q.prospectActivity(prospectId),
  });
  const log = useMutation({
    mutationFn: (type: "note_added" | "call_logged") =>
      api.post(`/v1/prospects/${prospectId}/activity`, { type, summary: note }),
    onSuccess: () => {
      setNote("");
      qc.invalidateQueries({ queryKey: ["activity", prospectId] });
      qc.invalidateQueries({ queryKey: ["prospect", prospectId] });
    },
  });
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card>
        <CardHeader>
          <CardTitle>Log</CardTitle>
          <CardDescription>
            Calls and notes count as activity and keep the prospect with you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Spoke with Alex — wants to see the kitchen display next week."
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!note.trim()}
              loading={log.isPending}
              onClick={() => log.mutate("call_logged")}
            >
              Log call
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!note.trim()}
              loading={log.isPending}
              onClick={() => log.mutate("note_added")}
            >
              Add note
            </Button>
          </div>
        </CardContent>
      </Card>
      <div className="lg:col-span-2">
        {act.data?.items.length === 0 ? <EmptyState title="No activity yet" /> : null}
        <ol className="relative space-y-4 border-l pl-5">
          {(act.data?.items ?? []).map((a: ActivityDTO) => (
            <li key={a.id} className="relative">
              <span
                className={`absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full ${a.type.startsWith("link_") || a.type.startsWith("portal_") ? "bg-success" : a.type.startsWith("sla_") || a.type === "missed" ? "bg-warning" : "bg-primary"}`}
              />
              <div className="text-sm">{a.summary}</div>
              <div className="text-xs text-muted-foreground">
                {fmtDateTime(a.at)} · {a.type.replace(/_/g, " ")}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/**
 * The website we built for this prospect (webbuilder / their own domain). Saved on
 * the prospect; the portal's Website Simulator shows it under "Your site".
 */
/**
 * The prospect's brand system as the demo will use it: colours, fonts and logo
 * read off their website by the import (editable here when the rep knows better).
 * Empty fields fall back to a palette picked from the name, so the demo is never
 * MyManager-blue — this card shows what is real vs. defaulted.
 */
/** Google Fonts families offered in the font pickers (free text still accepted). */
const GOOGLE_FONTS = [
  "Inter",
  "Playfair Display",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Poppins",
  "Oswald",
  "Raleway",
  "Merriweather",
  "Nunito",
  "Source Sans 3",
  "DM Sans",
  "Lora",
  "Work Sans",
  "Bebas Neue",
  "Josefin Sans",
  "Cormorant Garamond",
  "Libre Baskerville",
  "Quicksand",
  "Fraunces",
  "Space Grotesk",
  "Manrope",
  "Rubik",
  "Karla",
  "PT Serif",
  "Barlow",
  "Archivo",
  "Outfit",
  "Abril Fatface",
  "Pacifico",
  "Lobster",
];

function BrandCard({ prospect, onDone }: { prospect: ProspectDTO; onDone: () => void }) {
  const fields = [
    ["brandColor", "Primary colour", "#c8102e"],
    ["brandSecondaryColor", "Secondary colour", "#1f2937"],
    ["brandHeadingFont", "Heading font", "Playfair Display"],
    ["brandBodyFont", "Body font", "Inter"],
    ["logoUrl", "Logo", ""],
  ] as const;
  type Key = (typeof fields)[number][0];
  const current = (k: Key) => (prospect[k] ?? "") as string;
  const snapshot = () =>
    Object.fromEntries(fields.map(([k]) => [k, current(k)])) as Record<Key, string>;
  const [draft, setDraft] = useState<Record<Key, string>>(snapshot);
  // When the saved brand changes (import applied, logo uploaded, other tab saved) re-seed
  // ONLY the fields that changed, so an edit in progress on another field survives.
  const prev = useRef(snapshot());
  const saved = fields.map(([k]) => `${k}=${current(k)}`).join("|");
  // biome-ignore lint/correctness/useExhaustiveDependencies: `saved` is the flattened dependency
  useEffect(() => {
    const now = snapshot();
    setDraft((d) => {
      const next = { ...d };
      for (const [k] of fields) if (now[k] !== prev.current[k]) next[k] = now[k];
      return next;
    });
    prev.current = now;
  }, [saved]);
  const HEX = /^#[0-9a-fA-F]{6}$/;
  const bad = (k: Key, v: string) => /Color$/.test(k) && v.trim() !== "" && !HEX.test(v.trim());
  const dirty = fields.some(([k]) => draft[k].trim() !== current(k));
  const invalid = fields.some(([k]) => bad(k, draft[k]));
  const m = useMutation({
    mutationFn: () =>
      api.patch(
        `/v1/prospects/${prospect.id}`,
        Object.fromEntries(fields.map(([k]) => [k, draft[k].trim()])),
      ),
    onSuccess: onDone,
  });
  const swatch = (v: string) => (HEX.test(v.trim()) ? v.trim().toLowerCase() : undefined);
  // Re-read the site: overwrites every field the site answers, keeps the rest.
  const read = useMutation({
    mutationFn: () =>
      api.post<ProspectDTO & { brandRead: string[] }>(`/v1/prospects/${prospect.id}/brand/refresh`),
    onSuccess: (pr) => {
      setDraft(
        Object.fromEntries(fields.map(([k]) => [k, (pr[k] ?? "") as string])) as Record<
          Key,
          string
        >,
      );
      onDone();
    },
  });
  // Logo file → the api's asset store → saved on the prospect straight away.
  const logoInput = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (f: File) => {
      const fd = new FormData();
      fd.append("file", f);
      return api.upload<ProspectDTO>(`/v1/prospects/${prospect.id}/logo`, fd);
    },
    onSuccess: (pr) => {
      setDraft((d) => ({ ...d, logoUrl: pr.logoUrl ?? "" }));
      onDone();
    },
  });
  const set = (k: Key, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  const colourRow = (k: "brandColor" | "brandSecondaryColor", label: string, fallback: string) => (
    <div key={k} className="flex items-center gap-2 text-xs">
      <span className="w-[118px] shrink-0 text-muted-foreground">{label}</span>
      <input
        type="color"
        aria-label={`${label} picker`}
        title="Pick a colour"
        value={swatch(draft[k]) ?? fallback}
        onChange={(e) => set(k, e.target.value)}
        className="h-9 w-11 shrink-0 cursor-pointer rounded-md border bg-transparent p-0.5"
      />
      <Input
        aria-label={label}
        value={draft[k]}
        placeholder={`${fallback} (default)`}
        spellCheck={false}
        className={cn("max-w-[150px] font-mono", bad(k, draft[k]) && "border-destructive")}
        onChange={(e) => set(k, e.target.value)}
      />
      {draft[k] ? (
        <Button variant="ghost" size="sm" onClick={() => set(k, "")} title="Use the default">
          Default
        </Button>
      ) : null}
    </div>
  );
  const fontRow = (k: "brandHeadingFont" | "brandBodyFont", label: string, fallback: string) => (
    <div key={k} className="flex items-center gap-2 text-xs">
      <span className="w-[118px] shrink-0 text-muted-foreground">{label}</span>
      <Input
        aria-label={label}
        list="mm-google-fonts"
        value={draft[k]}
        placeholder={`${fallback} (default)`}
        className="max-w-[260px]"
        style={{ fontFamily: draft[k] ? `"${draft[k]}", ${fallback}, sans-serif` : undefined }}
        onChange={(e) => set(k, e.target.value)}
      />
      {draft[k] ? (
        <Button variant="ghost" size="sm" onClick={() => set(k, "")} title="Use the default">
          Default
        </Button>
      ) : null}
    </div>
  );
  const fontsToLoad = [draft.brandHeadingFont, draft.brandBodyFont]
    .map((f) => f.trim())
    .filter((f) => f && GOOGLE_FONTS.includes(f));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Brand</CardTitle>
        <CardDescription>
          Colours, fonts and logo the demo is themed with: the website, the online shop, the POS and
          the apps. Read from their website when the prospect is created and again at build time if
          still blank; blank fields use a default picked for this business.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {fontsToLoad.length ? (
          <link
            rel="stylesheet"
            href={`https://fonts.googleapis.com/css2?${fontsToLoad
              .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}`)
              .join("&")}&display=swap`}
          />
        ) : null}
        <datalist id="mm-google-fonts">
          {GOOGLE_FONTS.map((f) => (
            <option key={f} value={f} />
          ))}
        </datalist>

        <div className="flex items-start gap-3">
          <div
            className="flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40"
            style={{
              background: swatch(draft.brandColor) ? `${swatch(draft.brandColor)}14` : undefined,
            }}
          >
            {draft.logoUrl ? (
              <img
                src={draft.logoUrl}
                alt="Logo"
                className="max-h-16 max-w-[112px] object-contain"
              />
            ) : (
              <span className="text-[11px] text-muted-foreground">No logo</span>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-2 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={logoInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) upload.mutate(f);
                }}
              />
              <Button
                variant="outline"
                size="sm"
                loading={upload.isPending}
                onClick={() => logoInput.current?.click()}
              >
                <Upload className="h-3.5 w-3.5" /> Upload logo
              </Button>
              {draft.logoUrl ? (
                <Button variant="ghost" size="sm" onClick={() => set("logoUrl", "")}>
                  Remove
                </Button>
              ) : null}
              <span className="text-muted-foreground">PNG, SVG, JPG or WebP; up to 15 MB.</span>
            </div>
            <Input
              aria-label="Logo URL"
              value={draft.logoUrl}
              placeholder="or paste a logo URL (https://…/logo.png)"
              spellCheck={false}
              onChange={(e) => set("logoUrl", e.target.value)}
            />
            {upload.isError ? (
              <div className="text-destructive">
                {upload.error instanceof Error ? upload.error.message : "Upload failed"}
              </div>
            ) : null}
          </div>
        </div>

        {colourRow("brandColor", "Primary colour", "#c8102e")}
        {colourRow("brandSecondaryColor", "Secondary colour", "#1f2937")}
        {fontRow("brandHeadingFont", "Heading font", "Playfair Display")}
        {fontRow("brandBodyFont", "Body font", "Inter")}

        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-muted-foreground">
            Fonts are Google Fonts family names; pick one or type any.
            {read.isError ? (
              <span className="block text-destructive">
                {read.error instanceof Error ? read.error.message : "Could not read the site"}
              </span>
            ) : read.data ? (
              <span className="block">
                {read.data.brandRead.length
                  ? `Read from their site: ${read.data.brandRead.length} field${read.data.brandRead.length === 1 ? "" : "s"}.`
                  : "Their site gave no brand values; defaults stay."}
              </span>
            ) : null}
          </span>
          {prospect.website ? (
            <Button
              variant="ghost"
              size="sm"
              loading={read.isPending}
              title="Read colours, fonts and logo from their website again"
              onClick={() => read.mutate()}
            >
              Read from their site
            </Button>
          ) : null}
          <Button
            variant="outline"
            disabled={!dirty || invalid || m.isPending}
            onClick={() => m.mutate()}
          >
            {m.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
        {m.isError ? (
          <div className="text-xs text-destructive">Could not save. Try again.</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function BuiltSiteCard({
  prospectId,
  value,
  onDone,
}: {
  prospectId: string;
  value: string;
  onDone: () => void;
}) {
  const [url, setUrl] = useState(value);
  useEffect(() => setUrl(value), [value]);
  const normalize = (s: string) => {
    const t = s.trim();
    if (!t) return "";
    return /^https?:\/\//i.test(t) ? t : `https://${t}`;
  };
  const m = useMutation({
    mutationFn: (builtSiteUrl: string) =>
      api.patch(`/v1/prospects/${prospectId}`, { builtSiteUrl }),
    onSuccess: onDone,
  });
  const next = normalize(url);
  const dirty = next !== (value || "");
  return (
    <Card>
      <CardHeader>
        <CardTitle>Their new website</CardTitle>
        <CardDescription>
          Paste the domain of the site we built for them. The portal shows it under Website → "Your
          site".
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-2">
          <Input
            value={url}
            placeholder="https://their-restaurant.com"
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && dirty) m.mutate(next);
            }}
          />
          <Button variant="outline" disabled={!dirty || m.isPending} onClick={() => m.mutate(next)}>
            {m.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
        {value ? (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            {value.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <div className="text-xs text-muted-foreground">
            Not set: "Your site" shows the generated preview built from their imported data.
          </div>
        )}
        {m.isError ? (
          <div className="text-xs text-destructive">Could not save. Try again.</div>
        ) : null}
      </CardContent>
    </Card>
  );
}
